import { useState, useEffect } from "react";
import axios from "axios";
import { useLang } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import { MapPin, Plus, Search, Edit, Trash2, AlertTriangle, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const defaultForm = { device_id: "", client_id: "", plan: "monthly", price: "", start_date: "", end_date: "", status: "active", auto_renew: false };

function getDaysLeft(endDate) {
    if (!endDate) return null;
    const diff = new Date(endDate) - new Date();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export default function SubscriptionsPage() {
    const { t } = useLang();
    const { user } = useAuth();
    const [subs, setSubs] = useState([]);
    const [clients, setClients] = useState([]);
    const [devices, setDevices] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("all");
    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [form, setForm] = useState(defaultForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const canEdit = ["admin", "staff"].includes(user?.role);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const params = { limit: 100 };
            if (statusFilter && statusFilter !== "all") params.status = statusFilter;
            const [sRes, cRes, dRes] = await Promise.all([
                axios.get(`${API}/subscriptions`, { params, withCredentials: true }),
                axios.get(`${API}/clients`, { params: { limit: 500 }, withCredentials: true }),
                axios.get(`${API}/devices`, { params: { limit: 1000 }, withCredentials: true }),
            ]);
            setSubs(sRes.data.items); setTotal(sRes.data.total);
            setClients(cRes.data.items);
            setDevices(dRes.data.items);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    useEffect(() => { fetchAll(); }, [statusFilter]);

    const openAdd = () => { setEditItem(null); setForm(defaultForm); setError(""); setShowModal(true); };
    const openEdit = (item) => { setEditItem(item); setForm({ device_id: item.device_id, client_id: item.client_id, plan: item.plan, price: item.price.toString(), start_date: item.start_date?.substring(0, 10) || "", end_date: item.end_date?.substring(0, 10) || "", status: item.status, auto_renew: item.auto_renew || false }); setError(""); setShowModal(true); };

    const handleSubmit = async (e) => {
        e.preventDefault(); setSaving(true); setError("");
        try {
            const payload = { ...form, price: parseFloat(form.price) };
            if (editItem) {
                await axios.put(`${API}/subscriptions/${editItem.id}`, payload, { withCredentials: true });
            } else {
                await axios.post(`${API}/subscriptions`, payload, { withCredentials: true });
            }
            setShowModal(false); fetchAll();
        } catch (e) { setError(e.response?.data?.detail || t("error")); }
        setSaving(false);
    };

    const handleDelete = async (item) => {
        if (!window.confirm(t("confirm_delete"))) return;
        try {
            await axios.delete(`${API}/subscriptions/${item.id}`, { withCredentials: true });
            fetchAll();
        } catch (e) { alert(e.response?.data?.detail || t("error")); }
    };

    const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

    const getStatusClass = (sub) => {
        if (sub.status === "expired") return "badge-expired";
        const days = getDaysLeft(sub.end_date);
        if (days !== null && days < 0) return "badge-expired";
        if (days !== null && days <= 15) return "badge-pending";
        if (sub.status === "active") return "badge-active";
        return "badge-inactive";
    };

    const getStatusLabel = (sub) => {
        const days = getDaysLeft(sub.end_date);
        if (days !== null && days < 0) return `${t("overdue")} ${Math.abs(days)}d`;
        if (days !== null && days <= 15) return `${days} ${t("days_left")}`;
        return t(sub.status);
    };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                        <MapPin size={20} className="text-[#E7212E]" /> {t("subscriptions")}
                    </h1>
                    <p className="text-sm text-slate-500 mt-0.5">{total} abonime gjithsej</p>
                </div>
                {canEdit && (
                    <div className="flex items-center gap-2">
                        <button onClick={async () => {
                            try {
                                const res = await axios.get(`${API}/export/subscriptions.csv`, { withCredentials: true, responseType: "blob" });
                                const url = window.URL.createObjectURL(new Blob([res.data]));
                                const link = document.createElement("a"); link.href = url;
                                link.setAttribute("download", `abonimet-${new Date().toISOString().substring(0, 10)}.csv`);
                                document.body.appendChild(link); link.click(); link.remove();
                            } catch { alert("Gabim gjatë eksportimit"); }
                        }} data-testid="export-subscriptions-btn"
                            className="flex items-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium px-3 py-2 rounded transition-colors">
                            <Download size={14} /> CSV
                        </button>
                        <button onClick={openAdd} data-testid="add-subscription-btn"
                            className="flex items-center gap-2 bg-[#E7212E] hover:bg-[#C91C26] text-white text-sm font-medium px-4 py-2 rounded transition-colors">
                            <Plus size={15} /> {t("add")}
                        </button>
                    </div>
                )}
            </div>

            <div className="mb-4">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-48" data-testid="sub-status-filter">
                        <SelectValue placeholder={t("filter")} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t("all_statuses")}</SelectItem>
                        <SelectItem value="active">{t("active")}</SelectItem>
                        <SelectItem value="pending">{t("pending")}</SelectItem>
                        <SelectItem value="expired">{t("expired")}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="bg-white border border-slate-200 rounded overflow-hidden">
                <table className="w-full crm-table" data-testid="subscriptions-table">
                    <thead>
                        <tr>
                            <th>IMEI</th>
                            <th>{t("client")}</th>
                            <th>{t("plan")}</th>
                            <th>{t("price")}</th>
                            <th>{t("start_date")}</th>
                            <th>{t("end_date")}</th>
                            <th>{t("status")}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8} className="text-center py-10 text-slate-400">{t("loading")}</td></tr>
                        ) : subs.length === 0 ? (
                            <tr><td colSpan={8} className="text-center py-10 text-slate-400">{t("no_data")}</td></tr>
                        ) : subs.map(s => {
                            const days = getDaysLeft(s.end_date);
                            const isUrgent = days !== null && days < 0;
                            return (
                                <tr key={s.id} data-testid={`sub-row-${s.id}`} className={isUrgent ? "bg-rose-50/30" : ""}>
                                    <td>
                                        <div className="flex items-center gap-1.5">
                                            {isUrgent && <AlertTriangle size={12} className="text-rose-500 flex-shrink-0" />}
                                            <span className="font-mono text-sm">{s.imei || "—"}</span>
                                        </div>
                                    </td>
                                    <td className="font-medium text-sm">{s.client_name}</td>
                                    <td><span className="capitalize text-sm">{t(s.plan)}</span></td>
                                    <td className="font-mono text-sm font-medium">{s.price?.toLocaleString()} ALL</td>
                                    <td className="text-xs text-slate-500">{s.start_date?.substring(0, 10)}</td>
                                    <td className={`text-xs font-medium ${isUrgent ? "text-rose-600" : days !== null && days <= 15 ? "text-amber-600" : "text-slate-600"}`}>
                                        {s.end_date?.substring(0, 10)}
                                    </td>
                                    <td>
                                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${getStatusClass(s)}`}>
                                            {getStatusLabel(s)}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="flex items-center gap-1">
                                            {canEdit && (
                                                <button onClick={() => openEdit(s)}
                                                    className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700" data-testid={`edit-sub-${s.id}`}>
                                                    <Edit size={14} />
                                                </button>
                                            )}
                                            {user?.role === "admin" && (
                                                <button onClick={() => handleDelete(s)}
                                                    className="p-1.5 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600" data-testid={`delete-sub-${s.id}`}>
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editItem ? "Modifiko Abonim" : "Shto Abonim të Ri"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-3 mt-2">
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Pajisja (IMEI) *</label>
                            <Select value={form.device_id || "none"} onValueChange={v => f("device_id", v === "none" ? "" : v)} disabled={!!editItem}>
                                <SelectTrigger data-testid="sub-device-select"><SelectValue placeholder="Zgjidh pajisjen" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— Zgjidh —</SelectItem>
                                    {devices.map(d => <SelectItem key={d.id} value={d.id}>{d.imei} {d.model ? `(${d.model})` : ""}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("client")} *</label>
                            <Select value={form.client_id || "none"} onValueChange={v => f("client_id", v === "none" ? "" : v)}>
                                <SelectTrigger data-testid="sub-client-select"><SelectValue placeholder="Zgjidh klientin" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— Zgjidh —</SelectItem>
                                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("plan")}</label>
                                <Select value={form.plan} onValueChange={v => f("plan", v)}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="monthly">{t("monthly")}</SelectItem>
                                        <SelectItem value="yearly">{t("yearly")}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("price")} (ALL)</label>
                                <input type="number" step="0.01" required value={form.price} onChange={e => f("price", e.target.value)}
                                    data-testid="sub-price-input"
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("start_date")}</label>
                                <input type="date" required value={form.start_date} onChange={e => f("start_date", e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("end_date")}</label>
                                <input type="date" required value={form.end_date} onChange={e => f("end_date", e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("status")}</label>
                            <Select value={form.status} onValueChange={v => f("status", v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="active">{t("active")}</SelectItem>
                                    <SelectItem value="pending">{t("pending")}</SelectItem>
                                    <SelectItem value="expired">{t("expired")}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {error && <p className="text-rose-600 text-sm bg-rose-50 p-2 rounded">{error}</p>}
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setShowModal(false)}
                                className="px-4 py-2 border border-slate-200 rounded text-sm text-slate-700 hover:bg-slate-50">{t("cancel")}</button>
                            <button type="submit" disabled={saving} data-testid="save-sub-btn"
                                className="px-4 py-2 bg-[#E7212E] hover:bg-[#C91C26] text-white text-sm font-medium rounded disabled:opacity-60">
                                {saving ? "Duke ruajtur..." : t("save")}
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
